import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API } from '../../utils/api';
import { Users, Plus, Search, Edit, Trash2, X, ChevronUp, ChevronDown } from 'lucide-react';
import ConfirmModal from '../../components/modals/ConfirmModal';
import ErrorModal from '../../components/modals/ErrorModal';

const EmptyState = ({ icon: Icon, title, description, action }) => (
  <div className="flex flex-col items-center justify-center py-16 text-center">
    <div className="p-4 bg-slate-800 rounded-2xl mb-4">
      <Icon className="w-8 h-8 text-slate-500" />
    </div>
    <h3 className="text-lg font-semibold text-white mb-1">{title}</h3>
    <p className="text-slate-400 text-sm mb-4 max-w-md">{description}</p>
    {action}
  </div>
);

const ClientFormModal = ({ client, sites, onSave, onClose }) => {
  const [form, setForm] = useState(client || { first_name: '', last_name: '', email: '', company_name: '', phone: '', address_line_1: '', address_line_2: '', city: '', postcode: '', country: 'United Kingdom', vat_number: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.first_name?.trim() || !form.last_name?.trim() || !form.email?.trim()) return;
    setSaving(true);
    try { await onSave(form); } finally { setSaving(false); }
  };

  const isValid = form.first_name?.trim() && form.last_name?.trim() && form.email?.trim();
  const linkedSites = client?.id ? sites?.filter(s => s.client_id === client.id) : [];

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="client-form-modal">
      <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-slate-700 flex items-center justify-between">
          <h2 className="text-lg font-bold text-white">{client ? 'Edit Client' : 'New Client'}</h2>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          {linkedSites.length > 0 && (
            <div>
              <label className="block text-sm text-slate-400 mb-1">Linked {linkedSites.length === 1 ? 'Site' : 'Sites'}</label>
              <div className="flex flex-wrap gap-1.5" data-testid="client-linked-sites">
                {linkedSites.map(s => (
                  <span key={s.id} className="px-2.5 py-1 bg-slate-700 text-sm text-white rounded-lg">{s.site_name}</span>
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-sm text-slate-400 mb-1">First Name *</label><input className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="client-first-name" /></div>
            <div><label className="block text-sm text-slate-400 mb-1">Last Name *</label><input className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="client-last-name" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-sm text-slate-400 mb-1">Email *</label><input type="email" className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="client-email" /></div>
            <div><label className="block text-sm text-slate-400 mb-1">Phone</label><input className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="client-phone" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-sm text-slate-400 mb-1">Company Name</label><input className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="client-company-name" /></div>
            <div><label className="block text-sm text-slate-400 mb-1">VAT Number</label><input className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white" value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} data-testid="client-vat" /></div>
          </div>
          <div><label className="block text-sm text-slate-400 mb-1">Address Line 1</label><input className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white" value={form.address_line_1} onChange={(e) => setForm({ ...form, address_line_1: e.target.value })} /></div>
          <div><label className="block text-sm text-slate-400 mb-1">Address Line 2</label><input className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white" value={form.address_line_2} onChange={(e) => setForm({ ...form, address_line_2: e.target.value })} /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="block text-sm text-slate-400 mb-1">City</label><input className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
            <div><label className="block text-sm text-slate-400 mb-1">Postcode</label><input className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white" value={form.postcode} onChange={(e) => setForm({ ...form, postcode: e.target.value })} /></div>
            <div><label className="block text-sm text-slate-400 mb-1">Country</label><input className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} /></div>
          </div>
          <div><label className="block text-sm text-slate-400 mb-1">Notes</label><textarea className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white h-20 resize-none" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
        </div>
        <div className="p-4 border-t border-slate-700 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-slate-300 hover:text-white transition-colors" data-testid="client-form-cancel">Cancel</button>
          <button onClick={handleSave} disabled={saving || !isValid} className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-slate-600 text-white rounded-lg transition-colors" data-testid="client-form-save">{saving ? 'Saving...' : 'Save Client'}</button>
        </div>
      </div>
    </div>
  );
};

const SortIcon = ({ field, sortField, sortDir }) => {
  if (sortField !== field) return <ChevronUp className="w-3 h-3 text-slate-600" />;
  return sortDir === 'asc'
    ? <ChevronUp className="w-3 h-3 text-purple-400" />
    : <ChevronDown className="w-3 h-3 text-purple-400" />;
};

const AdminClientsPage = () => {
  const [clients, setClients] = useState([]);
  const [sites, setSites] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [clientModal, setClientModal] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [errorModal, setErrorModal] = useState({ show: false, title: '', message: '' });
  const [isDeleting, setIsDeleting] = useState(false);
  const [sortField, setSortField] = useState('last_name');
  const [sortDir, setSortDir] = useState('asc');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchClients = useCallback(async (search) => {
    try {
      const params = search ? { search } : {};
      const res = await axios.get(`${API}/quoting/clients`, { headers, params });
      setClients(res.data.clients || []);
    } catch (err) { console.error('Clients fetch error:', err); }
  }, []);

  const fetchSites = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/sites`, { headers });
      setSites(res.data || []);
    } catch (err) { console.error('Sites fetch error:', err); }
  }, []);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      await Promise.all([fetchClients(), fetchSites()]);
      setIsLoading(false);
    };
    load();
  }, []);

  useEffect(() => {
    fetchClients(searchTerm);
  }, [searchTerm, fetchClients]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortedClients = useMemo(() => {
    return [...clients].sort((a, b) => {
      const valA = (a[sortField] || '').toString().toLowerCase();
      const valB = (b[sortField] || '').toString().toLowerCase();
      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [clients, sortField, sortDir]);

  const handleSaveClient = async (formData) => {
    try {
      if (clientModal?.id) {
        await axios.put(`${API}/quoting/clients/${clientModal.id}`, formData, { headers });
      } else {
        await axios.post(`${API}/quoting/clients`, formData, { headers });
      }
      setClientModal(null);
      fetchClients(searchTerm);
    } catch (err) {
      setErrorModal({ show: true, title: 'Save Failed', message: err.response?.data?.detail || 'Failed to save client' });
    }
  };

  const handleDeleteClient = async (clientId) => {
    setIsDeleting(true);
    try {
      await axios.delete(`${API}/quoting/clients/${clientId}`, { headers });
      setDeleteConfirm(null);
      fetchClients(searchTerm);
    } catch (err) {
      setDeleteConfirm(null);
      const msg = err.response?.data?.detail || 'Failed to delete client';
      setErrorModal({ show: true, title: 'Cannot Delete Client', message: msg });
    } finally {
      setIsDeleting(false);
    }
  };

  const columns = [
    { key: 'first_name', label: 'First Name' },
    { key: 'last_name', label: 'Last Name' },
    { key: 'email', label: 'Email' },
    { key: 'company_name', label: 'Company' },
    { key: 'phone', label: 'Phone' },
    { key: 'city', label: 'City' },
  ];

  return (
    <div className="min-h-screen bg-slate-900 -m-8 p-8 space-y-6" data-testid="clients-page">
      <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 rounded-xl p-6 text-white">
        <h1 className="text-2xl sm:text-3xl font-bold mb-2">Clients</h1>
        <p className="text-purple-200">Manage your client directory</p>
      </div>

      <div>
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
          </div>
        ) : (
          <div className="space-y-4" data-testid="clients-content">
            <div className="flex items-center justify-between gap-4">
              <div className="relative flex-1 max-w-sm">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-10 pr-4 py-2 text-sm text-white placeholder-slate-500" placeholder="Search clients..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} data-testid="clients-search" />
              </div>
              <button onClick={() => setClientModal({})} className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors" data-testid="new-client-btn">
                <Plus className="w-4 h-4" /> New Client
              </button>
            </div>

            {clients.length === 0 ? (
              <EmptyState icon={Users} title="No clients found" description="Add your first client to start creating quotes and invoices" action={<button onClick={() => setClientModal({})} className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm">Add Client</button>} />
            ) : (
              <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
                <table className="w-full text-sm" data-testid="clients-table">
                  <thead>
                    <tr className="bg-slate-700/30">
                      {columns.map(col => (
                        <th
                          key={col.key}
                          onClick={() => handleSort(col.key)}
                          className="text-left px-4 py-3 text-slate-400 font-medium cursor-pointer hover:text-slate-200 select-none"
                          data-testid={`sort-${col.key}`}
                        >
                          <div className="flex items-center gap-1">
                            {col.label}
                            <SortIcon field={col.key} sortField={sortField} sortDir={sortDir} />
                          </div>
                        </th>
                      ))}
                      <th className="text-right px-4 py-3 text-slate-400 font-medium w-24">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedClients.map(c => (
                      <tr key={c.id} className="border-t border-slate-700/30 hover:bg-slate-700/20 transition-colors" data-testid={`client-row-${c.id}`}>
                        <td className="px-4 py-3 text-white font-medium">{c.first_name || '-'}</td>
                        <td className="px-4 py-3 text-white font-medium">{c.last_name || '-'}</td>
                        <td className="px-4 py-3 text-slate-300">{c.email || '-'}</td>
                        <td className="px-4 py-3 text-slate-300">{c.company_name || '-'}</td>
                        <td className="px-4 py-3 text-slate-300">{c.phone || '-'}</td>
                        <td className="px-4 py-3 text-slate-300">{c.city || '-'}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-1">
                            <button onClick={() => setClientModal(c)} className="p-1.5 text-slate-500 hover:text-white rounded-lg hover:bg-slate-700 transition-colors" data-testid={`edit-client-${c.id}`}><Edit className="w-4 h-4" /></button>
                            <button onClick={() => setDeleteConfirm({ id: c.id, label: c.company_name || `${c.first_name} ${c.last_name}` })} className="p-1.5 text-slate-500 hover:text-red-400 rounded-lg hover:bg-slate-700 transition-colors" data-testid={`delete-client-${c.id}`}><Trash2 className="w-4 h-4" /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {clientModal !== null && (
        <ClientFormModal
          client={clientModal.id ? clientModal : null}
          sites={sites}
          onSave={handleSaveClient}
          onClose={() => setClientModal(null)}
        />
      )}

      <ConfirmModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={() => handleDeleteClient(deleteConfirm?.id)}
        title="Delete Client"
        message={`Are you sure you want to delete ${deleteConfirm?.label || 'this client'}? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        isLoading={isDeleting}
      />

      <ErrorModal
        isOpen={errorModal.show}
        onClose={() => setErrorModal({ show: false, title: '', message: '' })}
        title={errorModal.title}
        message={errorModal.message}
      />
    </div>
  );
};

export default AdminClientsPage;
